import {
  AtomIcon,
  BookOpenTextIcon,
  CalendarXIcon,
  FileTextIcon,
  FlaskConicalIcon,
  GlobeIcon,
  KeyRoundIcon,
  LayersIcon,
  LeafIcon,
  MagnetIcon,
  MessageCircleQuestionIcon,
  PenLineIcon,
  PuzzleIcon,
  SproutIcon,
  SunIcon,
  TrendingDownIcon,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/nMXN71rukoz1Np8zH7tHv5/05fa8c38-08e3-4648-95ea-2609075e12da-mockup.webp'

export const packDescription =
  'Le pack SCIENCES PRÊTES\u00A0: six volumes du CP au CM2, dont le Laboratoire des petits scientifiques, des ressources numériques consultables sur ordinateur, tablette et smartphone, et cinq bonus inclus.'

export const heroGuarantees = ['Téléchargement numérique', 'Sans abonnement', 'Fichiers à conserver']

export const stats = [
  { value: '240', label: 'Pages dans les volumes principaux' },
  { value: '6', label: 'Volumes numériques' },
  { value: '5', label: 'Niveaux\u00A0: CP, CE1, CE2, CM1 et CM2' },
  { value: '5', label: 'Bonus inclus' },
]

export const differenceStats = [
  { value: 'Du CP au CM2', label: 'Des ressources organisées par niveau' },
  { value: '6 volumes numériques', label: 'Dont un laboratoire transversal' },
  { value: 'PDF imprimables', label: 'Des supports à consulter et à utiliser en classe' },
]

export const beforeItems = [
  'Des heures à préparer chaque séance depuis zéro en cherchant sur dix sites différents.',
  'Des expériences difficiles à organiser faute de matériel anticipé ou de consignes claires.',
  'Des difficultés à relier les explications aux observations des élèves.',
  'Des fiches et des réponses à préparer pour chaque activité.',
  'Des élèves qui décrochent lorsque les sciences restent trop abstraites.',
]

export const afterItems = [
  'Un guide à parcourir pour repérer le déroulement de votre séance.',
  'Un laboratoire transversal pour proposer des activités pratiques.',
  'Des fiches élèves regroupées dans le volume de votre niveau.',
  'Des corrigés pour accompagner les réponses et faciliter la correction.',
  'Des occasions de faire observer, questionner et raisonner vos élèves.',
]

export const recognizeItems: Array<{ icon: LucideIcon; title: string; text: string }> = [
  {
    icon: MessageCircleQuestionIcon,
    title: 'L’angoisse de l’explication',
    text: 'Arriver en classe sans savoir comment expliquer simplement une notion scientifique.',
  },
  {
    icon: CalendarXIcon,
    title: 'Les dimanches sacrifiés',
    text: 'Passer votre dimanche à préparer des supports pédagogiques au lieu de profiter de votre temps libre.',
  },
  {
    icon: PuzzleIcon,
    title: 'Le casse-tête des ressources',
    text: 'Chercher des fiches claires, visuelles et adaptées à votre niveau, puis devoir encore les assembler.',
  },
  {
    icon: PenLineIcon,
    title: 'La correction sans fin',
    text: 'Préparer les activités, puis rédiger vous-même les réponses pour pouvoir les corriger.',
  },
  {
    icon: TrendingDownIcon,
    title: 'Les élèves qui décrochent',
    text: 'Avoir du mal à rendre les sciences concrètes et à accompagner les différents rythmes de votre classe.',
  },
]

// Spine colours mirror the physical box (CP blue, CE1 red, CE2 yellow, CM1 green, CM2 purple, Labo grey).
export const volumes: Array<{
  number: string
  level: string
  title: string
  icon: LucideIcon
  spine: string
  rule: string
}> = [
  { number: 'Volume 01', level: 'CP', title: 'Observer et découvrir', icon: SproutIcon, spine: 'bg-blue-700 text-white', rule: 'border-white/30' },
  { number: 'Volume 02', level: 'CE1', title: 'Manipuler et comprendre', icon: GlobeIcon, spine: 'bg-red-600 text-white', rule: 'border-white/30' },
  { number: 'Volume 03', level: 'CE2', title: 'Chercher et expliquer', icon: SunIcon, spine: 'bg-amber-400 text-ink', rule: 'border-ink/20' },
  { number: 'Volume 04', level: 'CM1', title: 'Expérimenter et raisonner', icon: LeafIcon, spine: 'bg-emerald-700 text-white', rule: 'border-white/30' },
  { number: 'Volume 05', level: 'CM2', title: 'Relier et approfondir', icon: AtomIcon, spine: 'bg-violet-700 text-white', rule: 'border-white/30' },
  { number: 'Volume 06', level: 'CP–CM2', title: 'Le laboratoire des petits scientifiques', icon: MagnetIcon, spine: 'bg-slate-600 text-white', rule: 'border-white/30' },
]

export const volumesIntro = {
  title: '6 volumes numériques — Du CP au CM2',
  text: 'Une collection organisée pour accompagner les découvertes et le raisonnement scientifique à l’école élémentaire.',
}

export const packItems: Array<{ icon: LucideIcon; title: string; text: string }> = [
  {
    icon: FlaskConicalIcon,
    title: 'Laboratoire et activités pratiques',
    text: 'Un volume consacré à la pratique pour enrichir vos séances et donner une place à l’observation et à l’expérimentation.',
  },
  {
    icon: BookOpenTextIcon,
    title: 'Guides de séance prêts à consulter',
    text: 'Des indications pédagogiques pour vous aider à préparer votre intervention et à conduire les activités.',
  },
  {
    icon: FileTextIcon,
    title: 'Fiches élèves imprimables',
    text: 'Des supports réunis dans chaque volume pour faire travailler vos élèves sans créer chaque fiche depuis zéro.',
  },
  {
    icon: LayersIcon,
    title: 'Une organisation par niveau',
    text: 'Retrouvez vos ressources dans le volume correspondant à votre classe et sélectionnez celles qui s’intègrent à votre progression.',
  },
  {
    icon: KeyRoundIcon,
    title: 'Corrigés inclus',
    text: 'Des réponses et des repères pour faciliter la correction et accompagner vos élèves avec davantage de sérénité.',
  },
]

// Add the real page images (src) and captions when available.
export const pageSamples: Array<{ src: string; level: string; title: string; caption?: string }> = [
  { src: '', level: 'Volume 01 · CP', title: 'Observer et découvrir' },
  { src: '', level: 'Volume 02 · CE1', title: 'Manipuler et comprendre' },
  { src: '', level: 'Volume 03 · CE2', title: 'Chercher et expliquer' },
  { src: '', level: 'Volume 04 · CM1', title: 'Expérimenter et raisonner' },
  { src: '', level: 'Volume 05 · CM2', title: 'Relier et approfondir' },
  { src: '', level: 'Volume 06 · CP–CM2', title: 'Le laboratoire des petits scientifiques' },
]

export const pageTypes = ['Guide enseignant', 'Fiche élève', 'Corrigé', 'Activité de laboratoire']

export const bonuses = [
  {
    title: 'Le guide des classes à double niveau',
    text: 'Organisez vos séances lorsque vous devez accompagner deux niveaux dans une même classe.',
    benefit: 'Moins d’hésitations dans l’organisation, davantage de clarté pour préparer vos activités.',
  },
  {
    title: 'Les cartes pour parler comme un scientifique',
    text: 'Aidez vos élèves à mettre des mots sur leurs observations et à expliquer leurs idées.',
    benefit: 'Pour faire progresser le langage scientifique en même temps que les découvertes.',
  },
  {
    title: 'Le kit d’organisation des ateliers',
    text: 'Préparez le fonctionnement de vos ateliers pour mieux accompagner les élèves pendant les activités.',
    benefit: 'Moins de temps à gérer l’organisation, plus de disponibilité pour enseigner.',
  },
  {
    title: 'Les séances de remplacement prêtes à utiliser',
    text: 'Un remplacement ou un changement de planning\u00A0? Retrouvez un point de départ pour préparer votre intervention.',
    benefit: 'Pour ne pas vous retrouver devant une page blanche lorsque le temps manque.',
  },
  {
    title: 'Le pack sciences–familles',
    text: 'Facilitez le lien avec les familles autour des découvertes scientifiques.',
    benefit: 'Pour prolonger la curiosité au-delà de la classe.',
  },
]

export const offerRows = [
  {
    label: '6 volumes numériques du CP au CM2',
    detail: 'Avec le Laboratoire des petits scientifiques',
  },
  { label: '5 bonus complémentaires inclus' },
  { label: 'Guides enseignants · Fiches élèves · Corrigés' },
]

export const PRICE = '27\u00A0€'

// Only add authentic reviews of SCIENCES PRÊTES. The section stays hidden while empty.
export const testimonials: Array<{ quote: string; name: string; location: string }> = []

export const faqs = [
  {
    q: 'Comment vais-je recevoir le matériel\u00A0?',
    a: 'Vous recevez des fichiers numériques à télécharger. Vous pouvez consulter les documents sur votre ordinateur, votre tablette ou votre smartphone, et imprimer les fiches utiles à votre classe.',
  },
  {
    q: 'Est-ce adapté au collège\u00A0?',
    a: 'Cette édition est destinée à l’école élémentaire, du CP au CM2. Elle comprend cinq volumes par niveau et un laboratoire transversal.',
  },
  {
    q: 'Est-ce adapté aux élèves à besoins éducatifs particuliers\u00A0?',
    a: 'Vous pouvez adapter l’accompagnement, les consignes et le rythme aux besoins de vos élèves. Le pack ne constitue pas une édition spécialisée pour les troubles des apprentissages.',
  },
  {
    q: 'Les corrigés sont-ils inclus\u00A0?',
    a: 'Oui. Les volumes principaux comprennent des corrigés pour accompagner les activités et faciliter la correction.',
  },
  {
    q: 'Dois-je tout imprimer\u00A0?',
    a: 'Non. Vous pouvez consulter les volumes à l’écran et imprimer uniquement les pages nécessaires à la séance choisie.',
  },
  {
    q: 'Les bonus sont-ils compris dans le prix\u00A0?',
    a: 'Oui. Les cinq bonus présentés sur cette page sont inclus dans les 27\u00A0€.',
  },
  {
    q: 'Y a-t-il des frais mensuels\u00A0?',
    a: 'Non. Vous payez une seule fois, sans abonnement.',
  },
]

export const footerLinks = [
  'Conditions générales de vente',
  'Politique de confidentialité',
  'Politique de remboursement',
  'Contact',
]
